import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue";
const _hoisted_1 = { class: "px-5 pt-6 pb-4 flex flex-col flex-1 min-h-0" };
const _hoisted_2 = { class: "flex items-center justify-between mb-6 sm:mb-8" };
const _hoisted_3 = { class: "text-2xl sm:text-3xl font-bold text-gray-900 shrink-0 mr-60" };
const _hoisted_4 = { class: "flex items-center gap-3" };
const _hoisted_5 = { class: "shrink-0" };
const _hoisted_6 = {
    key: 0,
    class: "h-full flex flex-col"
};
import { Avatar, AdvancedFilterPopover, Button, DataTable, Pager, TableFilterSelector, TableShell, Tooltip, formatPhoneForDisplay, useTableQuery, useTableViews, } from "@eudonet/opal";
import { computed, h, onMounted, ref, watch } from "vue";
import CreateContactModal from "../components/CreateContactModal.vue";
import { useI18n } from "vue-i18n";
import { useFilters } from "@/shared/composables/useFilters";
import { useQuickFilterCatalog, useQuickFilters } from "@/shared/composables/useQuickFilters";
import { getFilterMetadata } from "@/shared/infrastructure/api/filterMetadataApi";
import { useAdvancedFilter } from "@/shared/composables/useAdvancedFilter";
import { useAdvancedFilterLabels } from "@/shared/composables/useAdvancedFilterLabels";
import { useSavedFilterLifecycle } from "@/shared/composables/useSavedFilterLifecycle";
import { AdvancedFilterLibraryModal, SavedFilterLifecycleModals, } from "@/shared/components/advanced-filter-library";
import { useAppToast } from "@/shared/composables/useAppToast";
import { useListViewPreferences } from "@/shared/composables/useListViewPreferences";
import { usePermissions, useListColumns, useListSettings, saveListNavContext } from "@/shared/composables";
import { useContacts } from "../../composables/useContacts";
import ModuleState from "@/shared/components/ModuleState.vue";
import ModuleLoading from "@/shared/components/ModuleLoading.vue";
import { RouterLink, useRoute } from "vue-router";
import { buildFilterSelectorLabels } from "@/shared/composables/filterSelectorLabels";
import { buildTableLabels } from "@/shared/composables/tableLabels";
import { useListRowSelection } from "@/shared/composables/useListRowSelection";
import ListFloatActionBar from "@/shared/components/ListFloatActionBar.vue";
import { CONTACT_DATE_ONLY_FIELDS, CONTACT_FILTER_VALUES } from "../../domain/contactFilterValues";
export default /*@__PURE__*/ _defineComponent({
    __name: 'Contact',
    setup(__props) {
        const { t } = useI18n();
        const filterSelectorLabels = computed(() => buildFilterSelectorLabels(t));
        const { canEditOphub: canEdit, disabledTooltip } = usePermissions();
        const showCreateModal = ref(false);
        const ROW_OPTIONS = [10, 30, 50];
        // ── OP-6491 "Filtrer" entry point — real OPQRY field catalog ─────────────────
        const { toastError } = useAppToast();
        const filterCatalog = useQuickFilterCatalog(() => getFilterMetadata("contact"), {
            rootGroupLabel: () => t("filters.groups.contacts"),
            options: CONTACT_FILTER_VALUES,
        });
        const { fields: filterFields, fieldGroups: filterFieldGroups, tableQueryOptions: filterTableQueryOptions, } = filterCatalog;
        // ── Table query & views ───────────────────────────────────────────────────────
        const tableQuery = useTableQuery({ pageSize: ROW_OPTIONS[0], ...filterTableQueryOptions });
        const { ready: quickFiltersReady, refinement: quickRefinement, onListFilterApply, loadErrorKey, } = useQuickFilters(tableQuery, filterCatalog, { storageKey: "contact", dateOnlyFields: CONTACT_DATE_ONLY_FIELDS });
        const tableViews = useTableViews({ initialViews: [] });
        // Seed the table search from the URL (?q=) so the header global-search "see all" link
        // deep-links into this list pre-filtered. `immediate` covers arriving fresh; the watcher
        // also re-seeds when a new ?q= is pushed while already on this page.
        const route = useRoute();
        watch(() => route.query.q, (q) => {
            const term = typeof q === "string" ? q : "";
            if (term && term !== tableQuery.state.search)
                tableQuery.state.search = term;
        }, { immediate: true });
        // ── Filters & per-user pinned pre-filtered lists ──────────────────────────────
        // `allLists` covers all scopes (eudonet / admin / myLists / shared); `filtersReady` gates restore.
        const { allLists, filtersReady } = useFilters("contact", "ophub");
        // ── OP-6506 Advanced filter builder ───────────────────────────────────────────
        const advancedFilter = useAdvancedFilter("contact");
        onMounted(() => {
            void advancedFilter.load();
        });
        // Anchors the OP-6869 popover under the "Filtrer" trigger instead of a centred modal.
        //
        // `anchorEl` is read from `filterSelectorRef` inside the `watch(builderOpen, ...)` below, not
        // reactively in the template or a `computed`: `filterSelectorRef` is written by Vue's own
        // `ref="filterSelectorRef"` mechanism as a side effect of the SAME render pass that mounts
        // `TableFilterSelector` (inside TableShell's filter slot). Making the RENDER EFFECT itself
        // depend on `filterSelectorRef` (a template read, or a `computed`/`watchEffect` that tracks
        // it) reliably corrupted the component tree in testing (observed as a crash deep in an
        // unrelated sibling during unmount) — it's concurrently written by the same pass that would
        // be reading it. A `watch(builderOpen, cb)` callback does NOT have this problem: only
        // `builderOpen` is a tracked dependency of the watch itself, so reading `filterSelectorRef.value`
        // inside the callback body is a plain, untracked property access — the same as reading it
        // inside a click handler — just centralized to one place instead of duplicated at every entry
        // point that can flip `builderOpen` to `true` (including `useSavedFilterLifecycle.onEditFilter`,
        // which sets it directly and has no `anchorEl` access of its own).
        const filterSelectorRef = ref(null);
        const anchorEl = ref(null);
        const advancedFilterPopoverRef = ref(null);
        const builderOpen = ref(false);
        const draftAst = ref(null);
        function openAdvancedBuilder() {
            draftAst.value = advancedFilter.appliedAst.value;
            builderOpen.value = true;
        }
        // `AdvancedFilterPopover` has no `@close` event — `update:open(false)` is its only close
        // signal, for every dismissal path (Escape, outside click, trigger re-click, and the
        // close-guard's confirmed "Fermer sans appliquer"). `lifecycle.closeBuilder()` is the single
        // "builder closed for any other reason than a successful apply/save" exit and already clears
        // `editingFilter` — apply/save keep closing directly via their own handlers below. The `open`
        // branch captures the trigger element (see the comment above `filterSelectorRef`) on every
        // entry point, not just `openAdvancedBuilder`/`openLibrary`.
        watch(builderOpen, (open) => {
            if (open) {
                anchorEl.value = filterSelectorRef.value?.triggerEl ?? null;
            }
            else {
                lifecycle.closeBuilder();
            }
        });
        function onApplyAdvanced(ast) {
            advancedFilter.applyAst(ast);
            // A real (non-empty) advanced filter takes precedence over an active saved view (useSearch
            // sends them as mutually exclusive on the wire) — deselect the view tab so the UI doesn't
            // show a saved view selected while ad-hoc results render.
            if (advancedFilter.appliedAst.value)
                tableQuery.setView(null);
            // Applying (instead of saving) must also leave edit mode — otherwise a stale
            // editingFilterId survives into the next builder session and a brand-new save
            // silently PUTs over the filter that was being edited (lifecycle.closeBuilder
            // clears both builderOpen and editingFilterId).
            lifecycle.closeBuilder();
        }
        // ── OP-6508 Advanced filter library ───────────────────────────────────────────
        const libraryOpen = ref(false);
        function openLibrary() {
            // Reachable from TWO independent triggers: the popover's own "view all" header link
            // (already routed through its internal requestClose/dirty-check before emitting view-all)
            // AND TableFilterSelector's own "Voir tous les filtres avancés" CTA — a SEPARATE control,
            // reachable even while the popover is open and dirty. Force-closing the popover here
            // (`builderOpen.value = false`) would silently discard unsaved criteria with no
            // confirmation on that second path. requestCloseThen() routes through the popover's own
            // guard instead — pristine closes silently, a dirty panel asks first — and only opens the
            // library once the panel has actually closed. It also leaves the popover "open" behind the
            // library modal if the user chooses to stay, which used to make
            // useSavedFilterLifecycle.onEditFilter's `if (... || builderOpen.value) return;` guard
            // silently discard the edit — a moot point now: if the user stays, the library never opens.
            const openLibraryNow = () => {
                libraryOpen.value = true;
            };
            if (advancedFilterPopoverRef.value) {
                advancedFilterPopoverRef.value.requestCloseThen(openLibraryNow);
            }
            else {
                builderOpen.value = false;
                openLibraryNow();
            }
        }
        // Apply a library filter server-side by its id (mutually exclusive with an inline ast).
        function onApplyLibraryFilter(id) {
            advancedFilter.clear();
            tableQuery.setView(id);
        }
        function onCreateFromLibrary() {
            openAdvancedBuilder();
        }
        // ── Saved-filter lifecycle (save / edit / rename / delete) ───────────────────
        const lifecycle = useSavedFilterLifecycle({
            entity: "contact",
            application: "ophub",
            draftAst,
            builderOpen,
            advancedFilter: { appliedAst: advancedFilter.appliedAst, clear: () => advancedFilter.clear() },
            tableQuery,
            onError: toastError,
            // Forward-reference: `reload` is declared further down (useContacts), but this
            // closure is only invoked later, on save — by then `reload` is already assigned.
            reloadList: () => reload(),
            // Every close this composable drives (closeBuilder, closeBuilderAndSelect via the
            // watch(builderOpen) above) bypasses the popover's own close()/builder.reset() — reset
            // its internal dirty state here too so the next open doesn't have to infer staleness.
            onBuilderClosed: () => advancedFilterPopoverRef.value?.discard(),
        });
        // Every user-visible string the OPAL builder needs (R29) — OPAL ships no copy.
        // Declared after `lifecycle` so the builder title can follow its editing state: the same
        // builder serves creation and edition, and `editingFilter` is what tells them apart —
        // its name is interpolated into the edition title (`filters.builder.editTitle`, OP-6885).
        const builderLabels = useAdvancedFilterLabels({
            editingName: () => lifecycle.editingFilter.value?.name,
        });
        // Pre-translated labels consumed by useListViewPreferences to build the tabDropdowns.
        const listLabels = computed(() => ({
            preFilteredLists: t("table.tabs.preFilteredLists"),
            eudonet: t("table.tabs.eudonet"),
            admin: t("table.tabs.admin"),
            myLists: t("table.tabs.myLists"),
            shared: t("table.tabs.sharedLists"),
            selected: t("table.tabs.selected"),
            searchPlaceholder: t("table.tabs.searchPlaceholder"),
            searchClearLabel: t("common.clearSearch"),
            lists: t("table.tabs.lists"),
            overflowTooltip: t("table.tabs.overflowTooltip"),
            createList: t("table.tabs.createList"),
            noListCreated: t("table.tabs.noListCreated"),
        }));
        // Contact default tabs = all Eudonet (System) lists — preserves today's behaviour where
        // every system filter appeared as a preset tab.
        const defaultPinnedIds = computed(() => allLists.value.filter((l) => l.scope === "eudonet").map((l) => l.id));
        const { tabDropdowns, reorder, restore } = useListViewPreferences({
            entity: "contact",
            tableQuery,
            tableViews,
            allLists,
            defaultPinnedIds,
            hasUrlView: () => !!route.query.view,
            labels: listLabels,
        });
        // Restore once filters have loaded (labels are needed to register pinned tabs as views).
        // Use `let` so the stop handle is available inside the immediate callback.
        let stopReady;
        stopReady = watch(filtersReady, (ready) => {
            if (!ready)
                return;
            void restore();
            stopReady?.();
        }, { immediate: true });
        // ── Column settings ───────────────────────────────────────────────────────────
        const columnSettings = useListSettings({
            entity: 'contact',
            columns: () => [
                {
                    key: 'fullName',
                    label: t('contact.columns.fullName'),
                    visible: true,
                    pinned: true,
                    disabled: true,
                },
                // Default-visible columns (OP-6122) in the order shown at first load.
                { key: 'salutation', label: t('contact.columns.salutation'), visible: true, pinned: false },
                { key: 'status', label: t('contact.columns.status'), visible: true, pinned: false },
                { key: 'category', label: t('contact.columns.category'), visible: true, pinned: false },
                { key: 'motivation', label: t('contact.columns.motivation'), visible: true, pinned: false },
                { key: 'relationLevel', label: t('contact.columns.relationLevel'), visible: true, pinned: false },
                { key: 'source', label: t('contact.columns.source'), visible: true, pinned: false },
                { key: 'preferredChannel', label: t('contact.columns.preferredChannel'), visible: true, pinned: false },
                // Opt-in columns (OP-6122) — hidden by default, available from the Columns menu.
                { key: 'birthDate', label: t('contact.columns.birthDate'), visible: false, pinned: false },
                { key: 'age', label: t('contact.columns.age'), visible: false, pinned: false },
                { key: 'email', label: t('contact.columns.email'), visible: false, pinned: false },
                { key: 'preferredLanguage', label: t('contact.columns.preferredLanguage'), visible: false, pinned: false },
                { key: 'contactFrequency', label: t('contact.columns.contactFrequency'), visible: false, pinned: false },
                { key: 'maidenName', label: t('contact.columns.maidenName'), visible: false, pinned: false },
                { key: 'gender', label: t('contact.columns.gender'), visible: false, pinned: false },
                { key: 'createdAt', label: t('contact.columns.createdAt'), visible: false, pinned: false },
                { key: 'updatedAt', label: t('contact.columns.updatedAt'), visible: false, pinned: false },
                { key: 'telephone', label: t('contact.columns.telephone'), visible: false, pinned: false },
            ],
        });
        // Restore saved sort after settings finish loading (OP-6130).
        watch(columnSettings.isLoading, (loading) => {
            if (!loading && columnSettings.activeSort.value) {
                const { column, direction } = columnSettings.activeSort.value;
                tableQuery.setSort(column, direction);
            }
        });
        // Clear sort when the user resets column settings (OP-6130).
        watch(columnSettings.activeSort, (sort) => {
            if (!columnSettings.isLoading.value && sort === null) {
                tableQuery.clearSort();
            }
        });
        // "select" (the row-checkbox column) is not user-managed: it leads the order and
        // the left-pinned cluster (tanstack honours the columnPinning.left order).
        const columnOrder = computed(() => ["select", ...columnSettings.order.value]);
        const columnPinning = computed(() => ({
            left: ["select", ...(columnSettings.pinning.value.left ?? []).filter((k) => k !== "select")],
            right: columnSettings.pinning.value.right ?? [],
        }));
        // ── Data loading ──────────────────────────────────────────────────────────────
        // Gate on settings-loaded and on the restored chips too, so the first fetch already carries the sort and the chips.
        const dataReady = computed(() => filtersReady.value && !columnSettings.isLoading.value && quickFiltersReady.value);
        const { contacts, pageResponse, loading, loadError, loadErrorCode, reload } = useContacts(tableQuery, dataReady, advancedFilter.appliedAst, quickRefinement);
        const loadErrorTitle = computed(() => t(loadErrorKey(loadErrorCode.value, "contact.loadError")));
        // ── Row selection + float action bar ─────────────────────────────────────────
        const { selectColumn, effectiveCount, allPagesSelected, totalAcrossAllPages, clearSelection, onSelectAllPages } = useListRowSelection({
            t,
            rows: () => contacts.value ?? [],
            getRowId: (contact) => contact.id,
            totalCount: () => pageResponse.value?.totalItems,
        });
        watch([contacts, pageResponse], ([list, page]) => {
            if (!page)
                return;
            saveListNavContext('contact', (list ?? []).map((c) => c.id), {
                query: {
                    filterId: advancedFilter.appliedAst.value ? null : tableQuery.state.activeViewId ?? undefined,
                    ast: advancedFilter.appliedAst.value ?? undefined,
                    refinement: quickRefinement.value ?? undefined,
                    search: tableQuery.state.search || undefined,
                    page: page.currentPage,
                    pageSize: page.pageSize,
                    sortBy: tableQuery.state.sort?.column,
                    sortOrder: tableQuery.state.sort?.direction,
                },
                pagination: {
                    currentPage: page.currentPage,
                    totalPages: page.totalPages,
                },
            });
        }, { immediate: true });
        // Activate the first selected item as the current view when the user picks a tab.
        function onTabRequested(_dropdownIndex, selectedItems) {
            const first = selectedItems[0];
            if (first) {
                // A saved view takes precedence over an active advanced filter — mirrors onApplyAdvanced's
                // reverse deselection so the two mechanisms stay mutually exclusive both ways. Without this,
                // useSearch keeps forcing filterId: null (an ast is still applied) and the tab looks inert.
                advancedFilter.clear();
                tableQuery.setView(first.id);
            }
        }
        // Persist the new drag-reorder for the given dropdown's segment.
        function onTabReordered(_dropdownIndex, orderedIds) {
            reorder(orderedIds);
        }
        // ── TableShell labels ─────────────────────────────────────────────────────────
        const tableLabels = computed(() => buildTableLabels(t));
        // ── Column helpers (shared with OrganisationList via useListColumns) ────────────
        const { formatDate, tOrRaw, formatLanguage, hideColumn, optionalColumn } = useListColumns({
            headerNamespace: 'contact.columns',
            columnSettings,
            minWidth: 130,
        });
        // ── Sort helpers ──────────────────────────────────────────────────────────────
        const sortItems = (key) => [
            { label: t('table.sortAZ'), onClick: () => {
                    tableQuery.setSort(key, 'asc');
                    columnSettings.handleSortChange({ column: key, direction: 'asc' });
                } },
            { label: t('table.sortZA'), onClick: () => {
                    tableQuery.setSort(key, 'desc');
                    columnSettings.handleSortChange({ column: key, direction: 'desc' });
                } },
        ];
        // Localized label for a Profile-block enum value (OP-6122). The BE sends raw PascalCase
        // member names ("CauseSensitivity"); the i18n option keys are lower-first camelCase.
        const profileOptionLabel = (field, value) => {
            if (!value)
                return '–';
            const key = value.charAt(0).toLowerCase() + value.slice(1);
            return tOrRaw(`contact.detail.profile.options.${field}.${key}`, value);
        };
        // Age in whole years derived from the birth date (OP-6122). Returns '–' when unknown.
        const formatAge = (birthDate) => {
            if (!birthDate)
                return '–';
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()))
                age -= 1;
            return age >= 0 ? String(age) : '–';
        };
        // ── Columns ───────────────────────────────────────────────────────────────────
        const contactColumns = [
            {
                id: 'fullName',
                header: t('contact.columns.fullName'),
                minWidth: 200,
                cell: ({ row }) => {
                    const { firstName, lastName } = row.original;
                    return h('div', { class: 'flex items-center gap-2' }, [
                        h(Avatar, { name: `${firstName} ${lastName}`, presentational: true }),
                        h(RouterLink, {
                            to: { name: 'ContactDetail', params: { id: row.original.id } },
                            class: 'text-blue-600 hover:underline',
                            'data-testid': `contact-row-link-${row.original.id}`,
                        }, () => `${firstName} ${lastName}`),
                    ]);
                },
                draggable: false,
                headerMenuItems: [...sortItems('fullName')],
            },
            {
                id: 'birthDate',
                header: t('contact.columns.birthDate'),
                minWidth: 130,
                cell: ({ row }) => formatDate(row.original.birthDate),
                draggable: true,
                headerMenuItems: [
                    ...sortItems('birthDate'),
                    { label: t('table.hideColumn'), onClick: () => hideColumn('birthDate') },
                ],
            },
            optionalColumn('salutation', ({ row }) => row.original.salutation
                ? tOrRaw(`contact.create.fields.salutation.options.${row.original.salutation.toLowerCase()}`, row.original.salutation)
                : '–'),
            {
                id: 'status',
                header: t('contact.columns.status'),
                minWidth: 130,
                cell: ({ row }) => row.original.status
                    ? tOrRaw(`contact.detail.status.${row.original.status.toLowerCase()}`, row.original.status)
                    : '–',
                draggable: true,
                headerMenuItems: [
                    ...sortItems('status'),
                    { label: t('table.hideColumn'), onClick: () => hideColumn('status') },
                ],
            },
            optionalColumn('category', ({ row }) => row.original.category
                ? tOrRaw(`contact.detail.profile.options.category.${row.original.category.toLowerCase()}`, row.original.category)
                : '–'),
            optionalColumn('motivation', ({ row }) => profileOptionLabel('motivation', row.original.motivation)),
            optionalColumn('relationLevel', ({ row }) => profileOptionLabel('relationLevel', row.original.relationLevel)),
            optionalColumn('source', ({ row }) => profileOptionLabel('source', row.original.source)),
            optionalColumn('preferredChannel', ({ row }) => profileOptionLabel('preferredChannel', row.original.preferredChannel)),
            optionalColumn('contactFrequency', ({ row }) => profileOptionLabel('contactFrequency', row.original.contactFrequency)),
            optionalColumn('age', ({ row }) => formatAge(row.original.birthDate)),
            optionalColumn('createdAt', ({ row }) => formatDate(row.original.createdAt)),
            optionalColumn('updatedAt', ({ row }) => formatDate(row.original.updatedAt)),
            optionalColumn('maidenName', ({ row }) => row.original.maidenName || '–'),
            {
                id: 'gender',
                header: t('contact.columns.gender'),
                minWidth: 130,
                cell: ({ row }) => {
                    const value = row.original.gender;
                    if (!value)
                        return '–';
                    // OPHUB Gender enum is PascalCase ("NonBinary"); the i18n options are lower-first camelCase.
                    const key = value.charAt(0).toLowerCase() + value.slice(1);
                    return tOrRaw(`contact.detail.options.gender.${key}`, value);
                },
                draggable: true,
                headerMenuItems: [
                    ...sortItems('gender'),
                    { label: t('table.hideColumn'), onClick: () => hideColumn('gender') },
                ],
            },
            optionalColumn('preferredLanguage', ({ row }) => formatLanguage(row.original.preferredLanguage)),
            optionalColumn('email', ({ row }) => row.original.primaryEmail || '–'),
            optionalColumn('telephone', ({ row }) => row.original.primaryPhone ? formatPhoneForDisplay(row.original.primaryPhone) : '–'),
        ];
        const columns = computed(() => [selectColumn, ...contactColumns]);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("h1", _hoisted_3, _toDisplayString(_ctx.$t("contact.title")), 1 /* TEXT */),
                        _createElementVNode("div", _hoisted_4, [
                            _createElementVNode("div", _hoisted_5, [
                                _createVNode(_unref(Tooltip), {
                                    text: _unref(disabledTooltip),
                                    disabled: _unref(canEdit)
                                }, {
                                    default: _withCtx(() => [
                                        _createVNode(_unref(Button), {
                                            label: _ctx.$t('contact.create.title'),
                                            "icon-left": "ri-add-line",
                                            disabled: !_unref(canEdit),
                                            "data-testid": "contact-create-button",
                                            onClick: _cache[0] || (_cache[0] = ($event) => (showCreateModal.value = true))
                                        }, null, 8 /* PROPS */, ["label", "disabled"])
                                    ]),
                                    _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["text", "disabled"])
                            ])
                        ])
                    ]),
                    _createVNode(_unref(TableShell), {
                        query: _unref(tableQuery),
                        views: _unref(tableViews),
                        labels: tableLabels.value,
                        permissions: { canSaveView: false, canShareView: false },
                        "tab-dropdowns": _unref(tabDropdowns),
                        "column-settings": _unref(columnSettings),
                        "show-view-selector": true,
                        onTabRequested: onTabRequested,
                        onTabReordered: onTabReordered,
                        onEditFilter: _cache[4] || (_cache[4] = (key) => filterSelectorRef.value?.openForField(key))
                    }, {
                        filter: _withCtx(() => [
                            _createVNode(_unref(TableFilterSelector), _mergeProps({
                                ref_key: "filterSelectorRef",
                                ref: filterSelectorRef
                            }, filterSelectorLabels.value, {
                                fields: _unref(filterFields),
                                "field-groups": _unref(filterFieldGroups),
                                "active-count": _unref(tableQuery).activeFilterCount.value,
                                "trigger-label": _ctx.$t('filters.button'),
                                "trigger-icon-left": "ri-filter-2-line",
                                "step1-search-placeholder": _ctx.$t('filters.searchPlaceholder'),
                                "step1-footer-label": _ctx.$t('filters.footerLabel'),
                                "view-advanced-label": _ctx.$t('filters.viewAdvanced'),
                                "create-advanced-label": _ctx.$t('filters.createAdvanced'),
                                "advanced-disabled": _unref(advancedFilter).isDisabled.value,
                                "advanced-disabled-tooltip": _ctx.$t('filters.comingSoon'),
                                "no-fields-label": _ctx.$t('filters.noFields'),
                                "fields-selectable": true,
                                "active-filters": _unref(tableQuery).state.filters ?? {},
                                "confirm-label": _ctx.$t('table.tabs.apply'),
                                "back-label": _ctx.$t('filters.quick.back'),
                                "no-results-label": _ctx.$t('filters.quick.noValueResults'),
                                "date-range-error-label": _ctx.$t('filters.quick.dateRangeError'),
                                onCreateAdvancedFilter: openAdvancedBuilder,
                                onViewAdvancedFilters: openLibrary,
                                onApply: _unref(onListFilterApply),
                                onApplyFilter: _cache[1] || (_cache[1] = (key, value) => _unref(tableQuery).setFilter(key, value)),
                                onRemoveFilter: _cache[2] || (_cache[2] = (key) => _unref(tableQuery).removeFilter(key))
                            }), null, 16 /* FULL_PROPS */, ["fields", "field-groups", "active-count", "trigger-label", "step1-search-placeholder", "step1-footer-label", "view-advanced-label", "create-advanced-label", "advanced-disabled", "advanced-disabled-tooltip", "no-fields-label", "active-filters", "confirm-label", "back-label", "no-results-label", "date-range-error-label", "onApply"])
                        ]),
                        table: _withCtx(() => [
                            (_unref(loading) || _unref(loadError) || _unref(contacts).length === 0)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                    (_unref(loading))
                                        ? (_openBlock(), _createBlock(ModuleLoading, { key: 0 }))
                                        : (_unref(loadError))
                                            ? (_openBlock(), _createBlock(ModuleState, {
                                                key: 1,
                                                type: "error",
                                                class: "flex-1",
                                                title: loadErrorTitle.value,
                                                "cta-icon": "ri-refresh-line",
                                                "cta-label": _unref(t)('common.retry'),
                                                onCta: _unref(reload)
                                            }, null, 8 /* PROPS */, ["title", "cta-label", "onCta"]))
                                            : (_openBlock(), _createBlock(ModuleState, {
                                                key: 2,
                                                type: "empty",
                                                class: "flex-1",
                                                title: _unref(t)('contact.empty'),
                                                "cta-icon": "ri-add-line",
                                                "cta-label": _unref(t)('contact.create.title'),
                                                "cta-disabled": !_unref(canEdit),
                                                "cta-disabled-tooltip": _unref(disabledTooltip),
                                                onCta: _cache[3] || (_cache[3] = ($event) => (showCreateModal.value = true))
                                            }, null, 8 /* PROPS */, ["title", "cta-label", "cta-disabled", "cta-disabled-tooltip"]))
                                ]))
                                : (_openBlock(), _createBlock(_unref(DataTable), {
                                    key: 1,
                                    columns: columns.value,
                                    data: _unref(contacts),
                                    "column-visibility": _unref(columnSettings).visibility,
                                    "column-order": columnOrder.value,
                                    "column-pinning": columnPinning.value,
                                    "column-sizing": _unref(columnSettings).widths.value,
                                    "has-settings": "",
                                    "sticky-header": "",
                                    onSettingsClick: _unref(columnSettings).openDrawer,
                                    "onUpdate:columnSizing": _unref(columnSettings).handleSizingChange
                                }, null, 8 /* PROPS */, ["columns", "data", "column-visibility", "column-order", "column-pinning", "column-sizing", "onSettingsClick", "onUpdate:columnSizing"]))
                        ]),
                        pagination: _withCtx(() => [
                            _createVNode(_unref(Pager), {
                                "current-page": _unref(pageResponse)?.currentPage ?? _unref(tableQuery).state.page,
                                "total-pages": _unref(pageResponse)?.totalPages ?? 0,
                                "total-items": _unref(pageResponse)?.totalItems ?? 0,
                                "items-per-page": _unref(tableQuery).state.pageSize,
                                "rows-per-page-options": ROW_OPTIONS,
                                "rows-per-page-label": _ctx.$t('pagination.rowsPerPage'),
                                "items-count-separator": _ctx.$t('pagination.of'),
                                "previous-label": _ctx.$t('pagination.previous'),
                                "next-label": _ctx.$t('pagination.next'),
                                "show-go-to-page": "",
                                "go-to-page-label": _ctx.$t('pagination.goToPage'),
                                "go-label": _ctx.$t('pagination.go'),
                                onPageChange: _unref(tableQuery).setPage,
                                onItemsPerPageChange: _unref(tableQuery).setPageSize
                            }, null, 8 /* PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "rows-per-page-label", "items-count-separator", "previous-label", "next-label", "go-to-page-label", "go-label", "onPageChange", "onItemsPerPageChange"])
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["query", "views", "labels", "tab-dropdowns", "column-settings"]),
                    _createVNode(ListFloatActionBar, {
                        "selection-count": _unref(effectiveCount),
                        "all-pages-selected": _unref(allPagesSelected),
                        "total-across-all-pages": _unref(totalAcrossAllPages),
                        onDismiss: _unref(clearSelection),
                        onSelectAllPages: _unref(onSelectAllPages)
                    }, null, 8 /* PROPS */, ["selection-count", "all-pages-selected", "total-across-all-pages", "onDismiss", "onSelectAllPages"])
                ]),
                _createVNode(_unref(AdvancedFilterPopover), {
                    ref_key: "advancedFilterPopoverRef",
                    ref: advancedFilterPopoverRef,
                    open: builderOpen.value,
                    "onUpdate:open": _cache[5] || (_cache[5] = ($event) => ((builderOpen).value = $event)),
                    modelValue: draftAst.value,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((draftAst).value = $event)),
                    "anchor-el": anchorEl.value,
                    fields: _unref(advancedFilter).builderFields.value,
                    "field-groups": _unref(advancedFilter).fieldGroups.value,
                    labels: _unref(builderLabels),
                    busy: _unref(lifecycle).savePending.value,
                    "show-save": "",
                    onApply: onApplyAdvanced,
                    onSave: _unref(lifecycle).onBuilderSave,
                    onViewAll: openLibrary
                }, null, 8 /* PROPS */, ["open", "modelValue", "anchor-el", "fields", "field-groups", "labels", "busy", "onSave"]),
                _createVNode(_unref(AdvancedFilterLibraryModal), {
                    modelValue: libraryOpen.value,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((libraryOpen).value = $event)),
                    entity: "contact",
                    application: "ophub",
                    "builder-fields": _unref(advancedFilter).builderFields.value,
                    onApply: onApplyLibraryFilter,
                    onCreate: onCreateFromLibrary,
                    onEdit: _unref(lifecycle).onEditFilter,
                    onRename: _unref(lifecycle).onRenameFilter,
                    onDelete: _unref(lifecycle).onDeleteFilter,
                    onManageAccess: _unref(lifecycle).onManageAccess
                }, null, 8 /* PROPS */, ["modelValue", "builder-fields", "onEdit", "onRename", "onDelete", "onManageAccess"]),
                _createVNode(_unref(SavedFilterLifecycleModals), { lifecycle: _unref(lifecycle) }, null, 8 /* PROPS */, ["lifecycle"]),
                _createVNode(CreateContactModal, {
                    modelValue: showCreateModal.value,
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => ((showCreateModal).value = $event))
                }, null, 8 /* PROPS */, ["modelValue"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
